import {createImageUrlBuilder, type ImageUrlBuilder} from "@sanity/image-url";

const imgUrlBuilder = createImageUrlBuilder({ projectId: process.env.NEXT_PUBLIC_SANITY_PROJECTID as string, dataset: process.env.NEXT_PUBLIC_SANITY_DATASET as string });

export function urlForAsset(assetRef: string): ReturnType<typeof createImageUrlBuilder> {
	return imgUrlBuilder.image(assetRef);
}

/** Original formats Sanity's CDN would send as-is to clients that don't list AVIF or WebP in Accept, at many times the bytes */
const FORCE_WEBP_EXTENSIONS = new Set(["png", "tif", "tiff", "bmp"]);

/**
 * The format to ask Sanity for, from the original's file type at the end of the asset id (image-<hash>-<w>x<h>-<ext>).
 * auto=format sends the original format to clients without AVIF/WebP in Accept: crawlers, link previews and an iOS
 * fetcher (NetworkingExtension) that took 2.7 MB per PNG on kino.dk. So PNG/TIFF/BMP originals get WebP for everyone
 * (fm overrides auto, so they lose AVIF in browsers), while the rest keep auto=format. GIF too, since WebP would lose the animation.
 */
export function getImageFormat(assetRef?: string | null): "webp" | "auto" {
	const ext = /-([a-z0-9]+)$/i.exec(assetRef ?? "")?.[1].toLowerCase();
	return ext && FORCE_WEBP_EXTENSIONS.has(ext) ? "webp" : "auto";
}

/** Sets the image format for the asset: fm=webp for PNG/TIFF/BMP originals, otherwise auto=format (see getImageFormat) */
export function withImageFormat(builder: ImageUrlBuilder, assetRef?: string | null): ImageUrlBuilder {
	return getImageFormat(assetRef) === "webp" ? builder.format("webp") : builder.auto("format");
}

/**
 * Like withImageFormat, for images other sites show (og:image): link previews don't all take WebP,
 * so PNG/TIFF/BMP originals get JPEG on a white background (for transparency) instead.
 */
export function withShareImageFormat(builder: ImageUrlBuilder, assetRef?: string | null): ImageUrlBuilder {
	return getImageFormat(assetRef) === "webp" ? builder.format("jpg").bg("fff") : builder.auto("format");
}
